import { rem } from 'polished'
import { useMemo, useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import type { ImageChangeHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import { trackEvent } from 'analytics/analytics'
import { carouselImageImpressionEvent } from 'analytics/snowplow/events'
import ImageWithReservedSpace from 'components/Common/ImageWithReservedSpace'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import OfferPageRoomGallery from 'components/OfferPage/OfferPageRoomGallery'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useRebrandOverlayBookmarkProps } from 'hooks/useRebrandOverlayButtonProps'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import WishlistGuard from 'wishlists/components/WishlistGuard'

import { mapRoomPackage } from './roomPackageUtils'

export const AccommodationRoomTileImageContainer = styled.div`
  grid-area: image;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  min-height: ${rem(200)};
`

const IMAGE_PROPS: ImageParams = {
  mobileWidth: '100vw',
  tabletWidth: '100vw',
  desktopWidth: '400px',
  largeDesktopWidth: '400px',
  quality: 'good',
  fit: 'center',
}

interface Props {
  className?: string
  roomPackage: App.Property.Room | App.HotelPackage | App.BedbankPackage
  // TODO(HP-1495): Accept either this or `App.Property.Property`.
  offer: App.HotelOffer | App.BedbankOffer
  /**
   * Whether to show the TripPlanner room bookmark button
   *
   * Defaults to `true` for `App.HotelPackage` and `App.BedbankPackage` room packages, and `false`
   * for `App.Property.Room` room packages.
   */
  showBookmark?: boolean
}

function AccommodationRoomTileImage(props: Props) {
  const {
    className,
    roomPackage,
    offer,
  } = props

  const intl = useIntl()
  const { iconKind, iconVariant } = useRebrandOverlayBookmarkProps()

  const [room, roomId, defaultShowBookmark] = mapRoomPackage<[App.Property.Room | App.PackageRoomType | App.BedbankPackage, string, boolean]>(
    roomPackage,
    propertyRoom => [propertyRoom, propertyRoom.id, false],
    hotelPackage => [hotelPackage.roomType, hotelPackage.roomType.id, true],
    bedbankPackage => [bedbankPackage, bedbankPackage.roomId, true],
  )
  const showBookmark = props.showBookmark ?? defaultShowBookmark

  const unavailableImageAltText = intl.formatMessage({
    id: 'idIA5EIkW6pLrEpizMrN',
    defaultMessage: 'Sorry this image is unavailable',
    description: 'Alt text for the room image placeholder shown when no images are available.',
  })

  const listId = useMemo(() => uuidv4(), [])

  const handleImageChange = useCallback<ImageChangeHandler>((index, image) => {
    if (offer && image) {
      trackEvent(
        carouselImageImpressionEvent(
          image,
          index + 1,
          'offer-package-gallery',
          listId,
          offer,
          // TODO(HP-1495): We may be emitting events with a `package_option` context comprising an
          // accommodation service room ID. This _won't_ be directly joinable with the `item`
          // context comprising an LPC/bedbank offer. For further details, see the discussion in
          // [this thread](https://luxgroup-hq.slack.com/archives/C05PGEGH39Q/p1764041531654779).
          roomId,
        ),
      )
    }
  }, [offer, roomId, listId])

  return <AccommodationRoomTileImageContainer className={className}>
    {!!room.images.length && <OfferPageRoomGallery
      room={room}
      imageParams={IMAGE_PROPS}
      onImageChange={handleImageChange}
      topRight={showBookmark && <CSSBreakpoint max="tablet">
        <WishlistGuard>
          <BookmarkButton
            withLabel="tablet"
            offer={offer}
            roomTypeId={roomId}
            iconKind={iconKind}
            iconVariant={iconVariant}
          />
        </WishlistGuard>
      </CSSBreakpoint>}
    />}
    {!room.images.length && <ImageWithReservedSpace
      id={NO_IMAGE_AVAILABLE_ID}
      {...IMAGE_PROPS}
      alt={unavailableImageAltText}
    />}
  </AccommodationRoomTileImageContainer>
}

export default withOfferPageAndReduxState((offerPageState) => {
  return {
    offer: (offerPageState.offer ?? offerPageState.bedbankOffer)!,
  }
}, AccommodationRoomTileImage)
